import React from "react";
import { useState, useEffect } from "react";
import {
   Pie,
   PieChart,
   Cell,
   ComposedChart,
   Bar,
   Line,
   BarChart,
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ReferenceLine,
   Legend,
   ResponsiveContainer,
   LabelList,
} from "recharts";
import {
   api,
   formatCurrency,
   getDateSpanArray,
   getStaticData,
   httpPost,
   parseOODate,
   parsePreisliste,
} from "../pureFunctions";
import { colorPalette100, header, host, averageString } from "../globalVariables";
import { useConfig } from "../ConfigContext";
import { Form, Col, Button, Row, Table } from "react-bootstrap";
import DatePicker from "react-datepicker";

export default function VerkaufsdatenPreisSpanne({accData, estateData }) {
   const config = useConfig();

   return (
      <div>
         {estateData && config.company === "baur" && (
            <div style={{ width: "100%", height: estateData.length * 32, padding: 30, marginTop: 30 }}>
               <h2 className="">Schätzspanne und Verkauftpreis</h2>
               <div className="mb-5">
                  {"Durchschnitt: "}
                  <strong>
                     {formatCurrency(
                        Math.round(
                           estateData
                              .filter((item) => item.kaufpreis)
                              .reduce((oldItem, item) => oldItem + (item?.kaufpreis || 0), 0) / estateData.length,
                        ),
                     )}
                  </strong>
                  {" | Median: "}{" "}
                  <strong>
                     {formatCurrency(
                        estateData.sort((a, b) => a.kaufpreis - b.kaufpreis)[Math.round(estateData.length / 2)]
                           ?.kaufpreis,
                     ) || "-"}
                  </strong>
                  {" | Summe "}
                  <strong>
                     {formatCurrency(
                        Math.round(
                           estateData
                              .filter((item) => item.kaufpreis)
                              .reduce((oldItem, item) => oldItem + (item?.kaufpreis || 0), 0),
                        ),
                     )}
                  </strong>{" "}
                  <small>(aktueller Ausschreibungspreis)</small>
                  <br />
                  {"Durchschnitt: "}
                  <strong>
                     {formatCurrency(
                        Math.round(
                           estateData
                              .filter((item) => item.verkauftFur)
                              .reduce((oldItem, item) => oldItem + (item?.verkauftFur || 0), 0) / estateData.length,
                        ),
                     )}
                  </strong>
                  {" | Median: "}{" "}
                  <strong>
                     {formatCurrency(
                        estateData.filter((item) => item.verkauftFur).sort((a, b) => a.verkauftFur - b.verkauftFur)[
                           Math.round(estateData.filter((item) => item.verkauftFur).length / 2)
                        ]?.verkauftFur,
                     ) || "-"}
                  </strong>
                  {" | Summe "}
                  <strong>
                     {formatCurrency(
                        Math.round(
                           estateData
                              .filter((item) => item.verkauftFur)
                              .reduce((oldItem, item) => oldItem + (item?.verkauftFur || 0), 0),
                        ),
                     )}
                  </strong>{" "}
                  <small>(Verkaufspreis)</small>
               </div>
               <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                     layout="vertical"
                     // style={{ width: "100%", maxWidth: "300px", maxHeight: "70vh", aspectRatio: 1 / 1.618 }}
                     // responsive
                     data={estateData}
                     margin={{
                        top: 20,
                        right: 0,
                        bottom: 0,
                        left: 0,
                     }}
                  >
                     <CartesianGrid strokeDasharray="3 3" />

                     <XAxis
                        orientation="top"
                        type="number"
                        // niceTicks="snap125"
                        xAxisId="gemeinsameAchse"
                        domain={["dataMin", (dataMax) => dataMax + 10000]}
                     />
                     <YAxis dataKey="name" type="category" />
                     <Tooltip
                        formatter={(value, name, props) => {
                           const {
                              spanne,
                              mittelwert,
                              objektBezeichnung,
                              preisVerlauf,
                              abweichung,
                              abweichungGrenzwert,
                           } = props.payload;

                           if (name === "Spanne") {
                              return [
                                 spanne[0]
                                    ? ` ${formatCurrency(spanne[0])}  - ${formatCurrency(spanne[1])} Mittelwert: ${mittelwert ? formatCurrency(mittelwert) : "---"} Kaufpreisabweichung (Mittelwert|Grenzwert): ${abweichung ?? "---"}% | ${abweichungGrenzwert ?? "---"}%`
                                    : "-",
                                 name,
                              ];
                           } else if (name === "Preisverlauf") {
                              return [
                                 preisVerlauf[0]
                                    ? ` ${formatCurrency(preisVerlauf[0])}  - ${formatCurrency(preisVerlauf[1])} `
                                    : "-",
                                 name,
                              ];
                           }
                           return [
                              `${formatCurrency(value)}
                              ${objektBezeichnung}`,
                              `${name}`,
                           ];
                        }}
                     />
                     <Legend />
                     <Bar
                        xAxisId="gemeinsameAchse"
                        dataKey="spanne"
                        name="Spanne"
                        fill="#00f"
                        fillOpacity={0.2}
                        stroke="#000"
                        barSize={20}
                     />
                     <Bar
                        xAxisId="gemeinsameAchse"
                        dataKey="preisVerlauf"
                        name="Preisverlauf"
                        fill="#f00"
                        fillOpacity={0.2}
                        stroke="#000"
                        barSize={8}
                     />
                     <Line
                        xAxisId="gemeinsameAchse"
                        dataKey="verkauftFur"
                        name="verkauft für"
                        stroke="#333"
                        strokeOpacity={0}
                        fill="#009"
                        dot={(props) => {
                           const { cx, cy, payload } = props;
                           const dotColor = accData[payload.priceLevel]?.color ?? "#f0f";

                           return (
                              <circle
                                 key={`dot-${payload.name}`}
                                 cx={cx} // X-Koordinate von Recharts
                                 cy={cy} // Y-Koordinate von Recharts
                                 r={8} // Radius
                                 fill={dotColor} // Dynamische Farbe!
                                 stroke="#000"
                                 strokeWidth={0}
                                 opacity={0.8}
                              />
                           );
                        }}
                        activeDot={(props) => {
                           const { cx, cy, payload } = props;
                           // const dotColor = payload.spanne[0] > 0 ? "#090" : "#f44";
                           const dotColor = accData[payload.priceLevel]?.color ?? "#f0f";

                           return (
                              <circle
                                 onClick={() => {
                                    window.open(
                                       `/datenblatt?estateid=${payload.estateId}&stufe=bewertungsbestellung&kachel=bewertungsbestellung`,
                                       "_blank",
                                    );
                                 }}
                                 key={`dot-${payload.name}`}
                                 cx={cx} // X-Koordinate von Recharts
                                 cy={cy} // Y-Koordinate von Recharts
                                 r={18} // Radius
                                 fill={dotColor} // Dynamische Farbe!
                                 stroke="#000"
                                 strokeWidth={0}
                                 opacity={1}
                              />
                           );
                        }}
                     />
                  </ComposedChart>
               </ResponsiveContainer>
            </div>
         )}
      </div>
   );
}
